import { type RefObject, useCallback, useRef } from "react";

import type { DateRange } from "@/v2/hooks/useDateParams";
import { isSameLocalDate, localDateKey } from "@/v2/lib/dates";
import type { EntriesScrollState } from "../types";

export interface UseEntriesAutoScrollOptions {
	date: Date;
	dateRange: DateRange;
	filteredEntryCount: number;
	pageRef: RefObject<HTMLDivElement>;
}

export function useEntriesAutoScroll({
	date,
	dateRange,
	filteredEntryCount,
	pageRef,
}: UseEntriesAutoScrollOptions): (node: HTMLDivElement | null) => void {
	const scrollStateRef = useRef<EntriesScrollState>({
		scopeKey: "",
		hasScrolledToTop: false,
	});
	const autoScrollToBottom =
		!dateRange.from && isSameLocalDate(date, new Date());
	const scrollScopeKey =
		dateRange.from || dateRange.to
			? `${localDateKey(dateRange.from)}:${localDateKey(dateRange.to)}`
			: localDateKey(date);

	return useCallback(
		(node: HTMLDivElement | null) => {
			if (!node || !pageRef.current || filteredEntryCount === 0) return;

			if (scrollStateRef.current.scopeKey !== scrollScopeKey) {
				scrollStateRef.current = {
					scopeKey: scrollScopeKey,
					hasScrolledToTop: false,
				};
			}

			if (autoScrollToBottom) {
				pageRef.current.scrollTop = pageRef.current.scrollHeight;
				return;
			}

			if (!scrollStateRef.current.hasScrolledToTop) {
				pageRef.current.scrollTop = 0;
				scrollStateRef.current.hasScrolledToTop = true;
			}
		},
		[autoScrollToBottom, filteredEntryCount, pageRef, scrollScopeKey],
	);
}
